import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getUserProfile } from '../api/services/profile/query';
import { PROFILE_QUERY_KEYS } from '@src/api/services/profile';
import { getAppStore } from '@src/utils/store-reference';
import { useUserRoleCapabilities, useModal } from '@stores';
import { useAuth } from '@hooks';
import isAnonymousModal from '@pages/Resources/components/modals/isAnonymous';
import NewApplicationModal from '@pages/Home/components/modals/NewApplication';
import ProfileModal from '@forms/components/modals/profile/Profile';
import { ProfileRoles, ResponseStatuses } from '@src/utils/enums';
import type { GetUserProfileResponse, UserProfile } from '@src/api/services/profile/types';
import { useApp } from '@src/stores/app.store';
import { loadUserRoleCapabilities } from '@api/services/user-role-capabilities/hooks/useUserRoleCapabilities';

type UseUserProfileReturn = {
  userRole: string | undefined;
  userProfileData: UserProfile | null;
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isUserDev: boolean;
  isUserRoleValid: boolean;
  userHasProfile: boolean;
  isUsaceRegulator: boolean;
  isExternalAgency: boolean;
  isHelpDesk: boolean;
};

export const useUserProfile = (): UseUserProfileReturn => {
  const store = getAppStore();
  const { isAuthenticated } = useAuth();

  const queryOptions: UseQueryOptions<GetUserProfileResponse, Error> = {
    queryKey: [PROFILE_QUERY_KEYS.GET_PROFILE],
    queryFn: async (): Promise<GetUserProfileResponse> => {
      const response = await getUserProfile();

      if (response.status === ResponseStatuses.UserFound) {
        try {
          await loadUserRoleCapabilities();
        } catch {
          useUserRoleCapabilities.getState().clearUserRoleCapabilities();
        }
      } else {
        useUserRoleCapabilities.getState().clearUserRoleCapabilities();
      }

      const VCSession = sessionStorage.getItem('VCSession');
      const resourcesModuleSession = sessionStorage.getItem('ResourcesModuleSession');
      const permitButtonSession = sessionStorage.getItem('PermitButtonSession');
      const canCreateRequest = useUserRoleCapabilities.getState().hasRoleCapability('createRequest');

      useApp.getState().setIsLoading(false);

      if (VCSession) {
        useModal.getState().openModal(isAnonymousModal, []);
      }

      if ((resourcesModuleSession || permitButtonSession) && canCreateRequest) {
        useModal.getState().openModal(NewApplicationModal, []);
      }

      if (response.status === ResponseStatuses.NoUserFound || response?.data?.role === ProfileRoles.ExternalAgency) {
        useModal.getState().openModal(ProfileModal, [{ edit: true }]);
      } else if (response.status !== ResponseStatuses.UserFound) {
        store.logout();
      }

      sessionStorage.removeItem('ResourcesModuleSession');
      sessionStorage.removeItem('PermitButtonSession');

      return response;
    },
    enabled: !!isAuthenticated,
  };

  const { data: userProfileResponse, isError, isLoading, isFetching } = useQuery(queryOptions);

  const userProfileData = userProfileResponse?.data || null;
  const userRole = userProfileData?.role;
  const isUserDev = userProfileData?.canUseDeveloperRole || false;
  const isUsaceRegulator = userProfileData?.role === ProfileRoles.USACERegulator;
  const isExternalAgency = userProfileData?.role === ProfileRoles.ExternalAgency;
  const isHelpDesk = userProfileData?.role === ProfileRoles.HelpDesk;
  const isUserRoleValid = !!userProfileData?.role && userProfileData.role !== ProfileRoles.ExternalAgency;
  const userHasProfile = userProfileResponse?.status === ResponseStatuses.UserFound;

  return {
    userRole,
    userProfileData,
    isError,
    isLoading,
    isFetching,
    isUserDev,
    isUserRoleValid,
    userHasProfile,
    isExternalAgency,
    isHelpDesk,
    isUsaceRegulator,
  };
};
